import React, { Component } from "react";
import "bootstrap/dist/css/bootstrap.css";
import "./Projects.css";

// Each card: title, optional label, short description, two or three bullets,
// a compact technology list, and optional expandable details and link.
const featuredProjects = [
  {
    title: "Proxmox Homelab",
    label: "Personal Homelab",
    description:
      "My personal IT learning environment for practicing Linux and Windows Server administration, virtualization, networking, monitoring, and troubleshooting.",
    bullets: [
      "Run virtual machines and LXC containers on Proxmox\u00a0VE",
      "Configure and troubleshoot Ubuntu Server, Rocky Linux, and Windows Server",
      "Run Pi-hole for DNS filtering and Uptime Kuma for uptime monitoring",
    ],
    tech: ["Proxmox VE", "Ubuntu Server", "Rocky Linux", "Windows Server", "Pi-hole", "Uptime Kuma", "SSH"],
  },
  {
    title: "Hosting My Personal Website",
    label: "Personal Project",
    description:
      "This React website runs in my homelab and updates automatically when I push changes.",
    bullets: [
      "Nginx in an LXC container serves the site",
      "Cloudflare Tunnel makes it reachable at elhacen.com",
      "GitHub Actions builds and deploys each update",
    ],
    details: [
      ["Debian LXC container", "a lightweight Linux container on Proxmox that runs the web server."],
      ["Nginx", "the web server that delivers the built website files to visitors."],
      ["Cloudflare Tunnel", "connects the web server to Cloudflare over an outbound connection, so the server does not need to accept inbound connections directly from the internet."],
      ["GitHub Actions", "when changes are pushed to the main branch, a workflow on a self-hosted runner installs dependencies, builds the site, and copies the build into the Nginx web directory."],
    ],
    tech: ["React", "Proxmox VE", "LXC", "Nginx", "Cloudflare Tunnel", "CI/CD", "GitHub Actions"],
  },
];

// TODO(verify): whether these two were personal projects or coursework.
const developmentProjects = [
  {
    title: "Employee Management CRUD App",
    description: "A full-stack web application for managing employee records.",
    bullets: [
      "ASP.NET Core Web API using Dapper and SQL Server for data access",
      "Angular front end for creating, viewing, updating, and deleting records",
    ],
    tech: ["ASP.NET Core", "C#", "Dapper", "SQL Server", "Angular"],
    link: "https://github.com/Elhacen18/dotnetproject",
  },
  {
    title: "Weather Forecasting App",
    description:
      "An Angular app that shows live weather for the user’s location or any searched city.",
    bullets: [
      "Finds the location with geolocation or Google Places autocomplete",
      "Pulls live conditions from the OpenWeatherMap API through a shared RxJS service",
      "Fixed a latitude/longitude parameter bug that returned weather for the wrong location",
    ],
    tech: ["Angular", "TypeScript", "OpenWeatherMap API", "Google Places API", "RxJS", "Angular Material"],
    link: "https://github.com/Elhacen18/WeatherApp",
  },
];

// TODO(verify): labels for ML Classification & Regression and Text Mining
// (coursework or personal). Only the NLP project is described as coursework.
const academicProjects = [
  {
    title: "ML Classification & Regression",
    description:
      "Compared scikit-learn classification and regression models across multiple datasets using cross-validation, confusion matrices, R², and mean squared error.",
    tech: ["Python", "scikit-learn", "Jupyter", "Pandas"],
    link: "https://github.com/Elhacen18/Machine_Learning",
  },
  {
    title: "NLP Sentiment & Text Analysis",
    label: "Coursework",
    description:
      "Graduate NLP project analyzing a document corpus for sentiment, readability, and linguistic features with Textatistic, TextBlob, and spaCy.",
    tech: ["Python", "TextBlob", "spaCy", "Textatistic"],
    link: "https://github.com/Elhacen18/PythonTextBlob",
  },
  {
    title: "Text Mining & Word Frequency Analysis",
    description:
      "Parsed a public-domain book, removed stop words, ranked word frequency in Pandas, and plotted the top words’ usage by chapter.",
    tech: ["Python", "Pandas", "Matplotlib"],
    link: "https://github.com/Elhacen18/Text_analysis",
  },
];

function GitHubLink({ project, compact = false }) {
  return (
    <a href={project.link} target="_blank" rel="noreferrer" className="TextLink ProjectLink">
      {compact ? "GitHub" : "View on GitHub"}
      <span className="visually-hidden">: {project.title} (opens in a new tab)</span>
      <span aria-hidden="true"> &rarr;</span>
    </a>
  );
}

function ProjectCard({ project, featured }) {
  return (
    <article className={featured ? "Card ProjectCard ProjectCardFeatured" : "Card ProjectCard"}>
      <h3 className="ProjectCardTitle">{project.title}</h3>
      {project.label && <span className="Label ProjectLabel">{project.label}</span>}
      <p className="ProjectDescription">{project.description}</p>
      <ul className="ProjectBullets">
        {project.bullets.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <ul className="TechList" aria-label="Technologies">
        {project.tech.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
      {(project.details || project.link) && (
        <div className="ProjectFooter">
          {project.details && (
            <details className="ProjectDetails">
              <summary>
                <span className="WhenClosed">View details</span>
                <span className="WhenOpen">Hide details</span>
              </summary>
              <ul>
                {project.details.map(([term, text]) => (
                  <li key={term}>
                    <strong>{term}:</strong> {text}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {project.link && <GitHubLink project={project} />}
        </div>
      )}
    </article>
  );
}

function ProjectGrid({ projects, featured = false }) {
  return (
    <div className="row g-4">
      {projects.map((project) => (
        <div className="col-12 col-md-6" key={project.title}>
          <ProjectCard project={project} featured={featured} />
        </div>
      ))}
    </div>
  );
}

class Projects extends Component {
  render() {
    return (
      <div className="Page">
        <div className="PageContainer ProjectsContainer">
          <h1 className="PageHeading">Projects</h1>
          <p className="PageIntro">
            My homelab work, development projects, and coursework. More on my{" "}
            <a
              href="https://github.com/Elhacen18"
              target="_blank"
              rel="noreferrer"
              className="InlineLink"
            >
              GitHub
              <span className="visually-hidden"> (opens in a new tab)</span>
            </a>
            .
          </p>

          <section aria-labelledby="featured-heading" className="ProjectSection">
            <h2 id="featured-heading" className="SectionHeading">
              Featured Homelab Projects
            </h2>
            <ProjectGrid projects={featuredProjects} featured />
          </section>

          <section aria-labelledby="development-heading" className="ProjectSection">
            <h2 id="development-heading" className="SectionHeading">
              Development Projects
            </h2>
            <ProjectGrid projects={developmentProjects} />
          </section>

          <section aria-labelledby="academic-heading" className="ProjectSection">
            <h2 id="academic-heading" className="SectionHeading SectionHeadingSecondary">
              Additional / Academic Projects
            </h2>
            <ul className="AcademicList">
              {academicProjects.map((project) => (
                <li className="AcademicItem" key={project.title}>
                  <div className="AcademicHeader">
                    <h3 className="AcademicTitle">{project.title}</h3>
                    {project.label && <span className="Label">{project.label}</span>}
                  </div>
                  <p className="AcademicDescription">{project.description}</p>
                  <div className="AcademicFooter">
                    <p className="AcademicTech">
                      <span className="visually-hidden">Technologies: </span>
                      {project.tech.join(" · ")}
                    </p>
                    <GitHubLink project={project} compact />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    );
  }
}

export default Projects;
